import { useState, useEffect, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { fmtINR, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, IndianRupee, Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, Boxes, Layers } from "lucide-react";

const TABS = [
  { key: "customers", label: "Customers" },
  { key: "locations", label: "Locations" },
  { key: "vendors", label: "Vendors" },
  { key: "pops", label: "POPs" },
  { key: "connections", label: "Connections" },
];

// Entities that support bulk import.
const IMPORTABLE = { customers: true, vendors: true, pops: true, connections: true };

const TABS_LABEL = { customers: "Customer", locations: "Location", vendors: "Vendor", pops: "POP", connections: "Connection", circuits: "Circuit" };

export default function Masters() {
  const { can } = useAuth();
  const [tab, setTab] = useState("customers");
  const [rows, setRows] = useState([]);
  const [refs, setRefs] = useState({ customers: [], locations: [], vendors: [], pops: [], users: [] });
  const [modal, setModal] = useState(null);
  const [itemsFor, setItemsFor] = useState(null); // {parentType:'connection'|'pop', row}
  const [importOpen, setImportOpen] = useState(false);
  const [enums, setEnums] = useState(null);

  const loadRefs = useCallback(async () => {
    const [c, l, v, p, u, e] = await Promise.all([
      api.get("/customers"), api.get("/locations"), api.get("/vendors"), api.get("/pops"),
      api.get("/users").catch(() => ({ data: [] })), api.get("/meta/enums"),
    ]);
    setRefs({ customers: c.data, locations: l.data, vendors: v.data, pops: p.data, users: u.data });
    setEnums(e.data);
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const load = useCallback(async () => {
    if (tab === "circuits") { const { data } = await api.get("/circuits?limit=100&sort=customer"); setRows(data.items); }
    else { const { data } = await api.get(`/${tab}`); setRows(data); }
  }, [tab]);
  useEffect(() => { load(); }, [load, refs.customers.length]);

  const save = async (payload) => {
    try {
      if (modal.mode === "edit") await api.put(`/${tab}/${modal.data.id}`, payload);
      else await api.post(`/${tab}`, payload);
      toast.success("Saved"); setModal(null); load();
      if (["customers", "locations", "vendors", "pops"].includes(tab)) loadRefs();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Archive this ${TABS_LABEL[tab]}?`)) return;
    try { await api.delete(`/${tab}/${row.id}`); toast.success("Archived"); load(); loadRefs(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const cols = {
    customers: [["name", "Name"], ["code", "Code"], ["account_manager", "Acct Mgr"], ["mobile", "Mobile"], ["status", "Status"]],
    locations: [["name", "Location"], ["customer_name", "Customer"], ["city", "City"], ["state", "State"], ["pin_code", "PIN"]],
    vendors: [["name", "Name"], ["code", "Code"], ["typical_billing_type", "Billing"], ["phone", "Phone"], ["active", "Active"]],
    pops: [["internal_pop_id", "POP ID"], ["name", "Name"], ["location", "Location"], ["pop_type", "Type"], ["upstream_count", "Feeds"], ["served_connections", "Serves"], ["at_risk", "Risk"]],
    connections: [["internal_connection_id", "Conn ID"], ["customer_name", "Customer"], ["delivery_type", "Delivery"], ["pop_name", "POP"], ["billable_item_count", "Items"], ["data_complete", "Complete"], ["status", "Status"]],
    circuits: [["internal_circuit_id", "Circuit ID"], ["customer_name", "Customer"], ["vendor_name", "ISP"], ["service_type", "Type"], ["billing_type", "Billing"], ["amount", "Amount"], ["status", "Status"]],
  };

  const cell = (r, key) => {
    if (key === "amount") return <span className="font-mono-num">{fmtINR(r[key])}</span>;
    if (key === "active") return r[key] ? "Yes" : "No";
    if (["internal_circuit_id", "internal_pop_id", "internal_connection_id"].includes(key)) return <span className="font-mono-num text-xs">{r[key]}</span>;
    if (key === "at_risk") return r[key] ? <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-semibold">AT RISK</span> : <span className="text-xs text-emerald-600">OK</span>;
    if (key === "data_complete") return r[key] ? <span className="text-xs text-emerald-600">✓</span> : <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700" title={(r.data_quality_flags || []).join(", ")}>Incomplete</span>;
    return r[key] ?? "—";
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head text-2xl font-extrabold">Masters</h1>
        <div className="flex items-center gap-2">
          {IMPORTABLE[tab] && can("create") && (
            <button onClick={() => setImportOpen(true)} data-testid="master-import"
              className="h-9 px-4 rounded-lg border border-slate-300 text-sm font-semibold flex items-center gap-2 hover:bg-slate-50"><Upload size={16} /> Import</button>
          )}
          {can("create") && tab !== "circuits" && <button onClick={() => setModal({ mode: "create", data: {} })} data-testid="master-add" className="h-9 px-4 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[var(--brand-hover)]"><Plus size={16} /> Add {TABS_LABEL[tab]}</button>}
        </div>
      </div>

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`master-tab-${t.key}`}
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === t.key ? "bg-white shadow-sm" : "text-slate-500"}`}>{t.label}</button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
            {cols[tab].map((c) => <th key={c[0]} className="px-4 py-3">{c[1]}</th>)}
            <th className="px-4 py-3 text-right">Actions</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                {cols[tab].map((c) => <td key={c[0]} className="px-4 py-3">{cell(r, c[0])}</td>)}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {tab === "connections" && (
                      <button onClick={() => setItemsFor({ parentType: "connection", row: r })} data-testid={`conn-items-${r.id}`}
                        title="Billable items" className="px-2 py-1 rounded-lg hover:bg-blue-50 text-blue-600 text-xs font-semibold flex items-center gap-1"><Layers size={14} /> Items</button>
                    )}
                    {tab === "pops" && (
                      <button onClick={() => setItemsFor({ parentType: "pop", row: r })} data-testid={`pop-feeds-${r.id}`}
                        title="Upstream feeds" className="px-2 py-1 rounded-lg hover:bg-blue-50 text-blue-600 text-xs font-semibold flex items-center gap-1"><Boxes size={14} /> Feeds</button>
                    )}
                    {can("edit") && tab !== "circuits" && <button onClick={() => setModal({ mode: "edit", data: r })} data-testid={`master-edit-${r.id}`} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={15} /></button>}
                    {can("delete") && tab !== "circuits" && <button onClick={() => remove(r)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={cols[tab].length + 1} className="px-4 py-10 text-center text-slate-400">No records.</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && <MasterForm tab={tab} modal={modal} refs={refs} enums={enums} onClose={() => setModal(null)} onSave={save} />}
      {itemsFor && <BillableItemsModal ctx={itemsFor} refs={refs} enums={enums} onClose={() => { setItemsFor(null); load(); }} />}
      {importOpen && <ImportModal entity={tab} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); loadRefs(); }} />}
    </div>
  );
}

function MasterForm({ tab, modal, refs, enums, onClose, onSave }) {
  const [f, setF] = useState(modal.data || {});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const field = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500";

  const fields = {
    customers: [["name", "Name*", "text"], ["code", "Customer Code", "text"], ["billing_name", "Billing Name", "text"], ["account_manager", "Account Manager", "text"], ["contact_person", "Contact Person", "text"], ["mobile", "Mobile", "text"], ["email", "Email", "text"], ["remarks", "Remarks", "text"]],
    locations: [["customer_id", "Customer*", "cust"], ["name", "Location Name*", "text"], ["address", "Address", "text"], ["city", "City", "text"], ["state", "State", "text"], ["pin_code", "PIN Code", "text"], ["site_contact", "Site Contact", "text"], ["site_contact_mobile", "Site Mobile", "text"]],
    vendors: [["name", "Name*", "text"], ["code", "Vendor Code", "text"], ["contact_person", "Contact Person", "text"], ["accounts_contact", "Accounts Contact", "text"], ["email", "Email", "text"], ["phone", "Phone", "text"], ["portal_url", "Portal URL", "text"], ["payment_method", "Payment Method", "text"], ["typical_billing_type", "Typical Billing", "billing"]],
    pops: [["name", "Name*", "text"], ["location", "Location*", "text"], ["pop_type", "Type*", "pop_type"], ["address", "Address", "text"], ["city", "City", "text"], ["state", "State", "text"], ["status", "Status", "cstatus"], ["remarks", "Remarks", "text"]],
    connections: [["customer_id", "Customer*", "cust"], ["location_id", "Location", "loc"], ["delivery_type", "Delivery Type*", "delivery"], ["pop_id", "POP (for Own-POP types)", "pop"], ["name", "Connection Name", "text"], ["status", "Status", "cstatus"], ["remarks", "Remarks", "text"]],
  };

  const render = (key, label, type) => {
    const common = { value: f[key] ?? "", onChange: (e) => set(key, e.target.value), className: field, "data-testid": `form-${key}` };
    if (type === "cust") return <select {...common}><option value="">Select…</option>{refs.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>;
    if (type === "loc") return <select {...common}><option value="">Select…</option>{refs.locations.filter((l) => !f.customer_id || l.customer_id === f.customer_id).map((c) => <option key={c.id} value={c.id}>{c.name} — {c.city}</option>)}</select>;
    if (type === "vendor") return <select {...common}><option value="">Select…</option>{refs.vendors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>;
    if (type === "pop") return <select {...common}><option value="">—</option>{refs.pops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>;
    if (type === "billing") return <select {...common}><option value="">—</option>{enums?.billing_types.map((s) => <option key={s}>{s}</option>)}</select>;
    if (type === "pop_type") return <select {...common}>{enums?.pop_types?.map((s) => <option key={s}>{s}</option>)}</select>;
    if (type === "delivery") return <select {...common}><option value="">Select…</option>{enums?.delivery_types?.map((s) => <option key={s}>{s}</option>)}</select>;
    if (type === "cstatus") return <select {...common}>{enums?.connection_statuses?.map((s) => <option key={s}>{s}</option>)}</select>;
    return <input type={type} {...common} />;
  };

  const submit = () => onSave({ ...f });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="master-form">
        <div className="flex items-center justify-between p-5 border-b"><h3 className="font-head font-bold text-lg">{modal.mode === "edit" ? "Edit" : "New"} {TABS_LABEL[tab]}</h3><button onClick={onClose} className="text-slate-400"><X /></button></div>
        <div className="p-5 grid grid-cols-2 gap-4 overflow-y-auto">
          {fields[tab].map(([k, l, t]) => (
            <div key={k} className={(k === "address" || k === "remarks") ? "col-span-2" : ""}>
              <label className="text-sm font-medium">{l}</label>
              {render(k, l, t)}
            </div>
          ))}
        </div>
        <div className="p-5 border-t flex justify-end gap-3"><button onClick={onClose} className="px-4 h-10 rounded-lg border text-sm">Cancel</button><button onClick={submit} data-testid="master-save" className="px-5 h-10 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold">Save</button></div>
      </div>
    </div>
  );
}

// ---- Billable items under a Connection or POP ----
function BillableItemsModal({ ctx, refs, enums, onClose }) {
  const { can } = useAuth();
  const { parentType, row } = ctx;
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null); // {mode, data}
  const [ratesFor, setRatesFor] = useState(null);

  const load = useCallback(async () => {
    const url = parentType === "connection" ? `/connections/${row.id}` : `/pops/${row.id}`;
    const { data } = await api.get(url);
    setDetail(data);
  }, [parentType, row.id]);
  useEffect(() => { load(); }, [load]);

  const items = detail ? (parentType === "connection" ? detail.billable_items : detail.upstream_feeds) || [] : [];

  const save = async (payload) => {
    try {
      const body = { ...payload };
      if (parentType === "connection") { body.connection_id = row.id; body.pop_id = null; }
      else { body.pop_id = row.id; body.connection_id = null; body.item_type = "POP Upstream"; }
      ["amount", "renewal_cycle_days"].forEach((k) => { if (body[k] !== undefined && body[k] !== "") body[k] = Number(body[k]); });
      if (form.mode === "edit") await api.put(`/billable-items/${form.data.id}`, body);
      else await api.post("/billable-items", body);
      toast.success("Saved"); setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (it) => {
    if (!window.confirm("Archive this billable item?")) return;
    try { await api.delete(`/billable-items/${it.id}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="items-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-head font-bold text-lg">{parentType === "connection" ? "Billable Items" : "Upstream Feeds"}</h3>
            <p className="text-sm text-slate-500">{row.internal_connection_id || row.internal_pop_id} · {row.customer_name || row.name}
              {parentType === "connection" && row.delivery_type ? ` · ${row.delivery_type}` : ""}</p>
          </div>
          <button onClick={onClose} className="text-slate-400"><X /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {can("create") && <button onClick={() => setForm({ mode: "create", data: { billing_type: parentType === "pop" ? "Postpaid" : "Prepaid", item_type: parentType === "pop" ? "POP Upstream" : "ILL", status: "Active" } })} data-testid="item-add" className="mb-3 h-9 px-4 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold inline-flex items-center gap-2"><Plus size={16} /> Add {parentType === "pop" ? "Feed" : "Billable Item"}</button>}
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500">
                <th className="px-3 py-2">BI ID</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Vendor</th><th className="px-3 py-2">Bandwidth</th><th className="px-3 py-2">Billing</th><th className="px-3 py-2">ARC</th><th className="px-3 py-2">Next</th><th className="px-3 py-2 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100">
                    <td className="px-3 py-2 font-mono-num text-xs">{it.internal_billable_id}</td>
                    <td className="px-3 py-2">{it.item_type}</td>
                    <td className="px-3 py-2">{it.vendor_name ?? "—"}</td>
                    <td className="px-3 py-2">{it.bandwidth ?? "—"}</td>
                    <td className="px-3 py-2">{it.billing_type}</td>
                    <td className="px-3 py-2 font-mono-num">{fmtINR(it.arc)}</td>
                    <td className="px-3 py-2">{fmtDate(it.next_action_date)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {can("manage_rates") && <button onClick={() => setRatesFor(it)} data-testid={`item-rates-${it.id}`} title="Rate" className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-600"><IndianRupee size={14} /></button>}
                        {can("edit") && <button onClick={() => setForm({ mode: "edit", data: it })} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Pencil size={14} /></button>}
                        {can("delete") && <button onClick={() => remove(it)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400">No billable items yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {form && <BillableItemForm parentType={parentType} form={form} refs={refs} enums={enums} onClose={() => setForm(null)} onSave={save} />}
      {ratesFor && <RatesModal item={ratesFor} onClose={() => setRatesFor(null)} onSaved={() => { setRatesFor(null); load(); }} />}
    </div>
  );
}

function BillableItemForm({ parentType, form, refs, enums, onClose, onSave }) {
  const [f, setF] = useState(form.data || {});
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const field = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500";
  const fields = [
    ["item_type", "Type*", "bitype"], ["vendor_id", "Vendor*", "vendor"], ["bandwidth", "Bandwidth", "text"],
    ["billing_type", "Billing Type*", "billing"], ["renewal_cycle", "Renewal Cycle", "cycle"], ["renewal_cycle_days", "Custom Days", "number"],
    ["amount", "Amount (₹)", "number"], ["isp_account_number", "ISP Account No.", "text"], ["isp_circuit_id", "ISP Circuit ID", "text"],
    ["registered_mobile", "Registered Mobile", "text"], ["plan_name", "Plan Name", "text"],
    ["current_expiry_date", "Current Expiry (Prepaid)", "date"], ["next_payment_due", "Next Payment Due (Postpaid)", "date"],
    ["status", "Status", "cstatus"],
  ];
  const render = (key, type) => {
    const common = { value: f[key] ?? "", onChange: (e) => set(key, e.target.value), className: field, "data-testid": `bi-${key}` };
    if (type === "vendor") return <select {...common}><option value="">Select…</option>{refs.vendors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>;
    if (type === "bitype") return <select {...common} disabled={parentType === "pop"}>{(parentType === "pop" ? ["POP Upstream"] : enums?.billable_item_types || []).map((s) => <option key={s}>{s}</option>)}</select>;
    if (type === "billing") return <select {...common}>{enums?.billing_types.map((s) => <option key={s}>{s}</option>)}</select>;
    if (type === "cycle") return <select {...common}><option value="">—</option>{enums?.renewal_cycles.map((s) => <option key={s}>{s}</option>)}</select>;
    if (type === "cstatus") return <select {...common}>{enums?.connection_statuses?.map((s) => <option key={s}>{s}</option>)}</select>;
    return <input type={type} {...common} />;
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="bi-form">
        <div className="flex items-center justify-between p-5 border-b"><h3 className="font-head font-bold text-lg">{form.mode === "edit" ? "Edit" : "New"} Billable Item</h3><button onClick={onClose} className="text-slate-400"><X /></button></div>
        <div className="p-5 grid grid-cols-2 gap-4 overflow-y-auto">
          {fields.map(([k, l, t]) => (
            <div key={k}><label className="text-sm font-medium">{l}</label>{render(k, t)}</div>
          ))}
          <p className="col-span-2 text-xs text-slate-400">ARC / OTC are set via the ₹ Rates action (requires manage_rates) after saving.</p>
        </div>
        <div className="p-5 border-t flex justify-end gap-3"><button onClick={onClose} className="px-4 h-10 rounded-lg border text-sm">Cancel</button><button onClick={() => onSave({ ...f })} data-testid="bi-save" className="px-5 h-10 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold">Save</button></div>
      </div>
    </div>
  );
}

function RatesModal({ item, onClose, onSaved }) {
  const [arc, setArc] = useState(item.arc ?? "");
  const [otc, setOtc] = useState(item.otc ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const field = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500";
  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required to change a rate."); return; }
    const body = { reason: reason.trim() };
    if (arc !== "") body.arc = Number(arc);
    if (otc !== "") body.otc = Number(otc);
    if (body.arc === undefined && body.otc === undefined) { toast.error("Enter an ARC or OTC value."); return; }
    setBusy(true);
    try { await api.post(`/billable-items/${item.id}/rates`, body); toast.success("Rate updated — logged to audit"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="rates-modal">
        <div className="flex items-center justify-between p-5 border-b"><h3 className="font-head font-bold text-lg flex items-center gap-2"><IndianRupee size={18} className="text-emerald-600" /> Manage Rate</h3><button onClick={onClose} className="text-slate-400"><X /></button></div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-slate-500">{item.internal_billable_id} · {item.item_type} · {item.vendor_name}</div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-sm font-medium">ARC — Annual (₹)</label><input type="number" value={arc} onChange={(e) => setArc(e.target.value)} className={field} data-testid="rates-arc" /><div className="text-[11px] text-slate-400 mt-1">Current: {fmtINR(item.arc)}</div></div>
            <div><label className="text-sm font-medium">OTC — One-Time (₹)</label><input type="number" value={otc} onChange={(e) => setOtc(e.target.value)} className={field} data-testid="rates-otc" /><div className="text-[11px] text-slate-400 mt-1">Current: {fmtINR(item.otc)}</div></div>
          </div>
          <div><label className="text-sm font-medium">Reason for change*</label><textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" placeholder="e.g. FY26 contract revision" data-testid="rates-reason" /></div>
        </div>
        <div className="p-5 border-t flex justify-end gap-3"><button onClick={onClose} className="px-4 h-10 rounded-lg border text-sm">Cancel</button><button onClick={submit} disabled={busy} data-testid="rates-save" className="px-5 h-10 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-60">Save Rate</button></div>
      </div>
    </div>
  );
}

function ImportModal({ entity, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  // "connections" tab imports connections; billable items imported from their own entity.
  const [target, setTarget] = useState(entity === "connections" ? "connections" : entity);
  const CHOICES = entity === "connections" ? [["connections", "Connections"], ["billable_items", "Billable Items"]] : [[entity, entity]];

  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/import/template/${target}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `nextra_import_${target}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download template"); }
  };
  const validate = async (f) => {
    setBusy(true); setResult(null);
    try { const fd = new FormData(); fd.append("file", f); const r = await api.post(`/import/entity/${target}/validate`, fd); setResult(r.data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const onPick = (e) => { const f = e.target.files?.[0]; if (!f) return; setFile(f); validate(f); };
  const commit = async () => {
    if (!file) return; setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post(`/import/entity/${target}/commit`, fd); toast.success(`Imported ${r.data.imported} ${target}`); onDone(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d?.errors) { setResult({ ok: false, errors: d.errors, valid: d.valid }); toast.error(d.message || "Import rejected"); }
      else toast.error(apiError(d));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="import-modal">
        <div className="flex items-center justify-between p-5 border-b"><h3 className="font-head font-bold text-lg flex items-center gap-2"><FileSpreadsheet size={18} className="text-[var(--brand)]" /> Import</h3><button onClick={onClose} className="text-slate-400"><X /></button></div>
        <div className="p-5 space-y-4 overflow-y-auto">
          {CHOICES.length > 1 && (
            <div className="flex gap-2">{CHOICES.map(([k, l]) => (
              <button key={k} onClick={() => { setTarget(k); setResult(null); setFile(null); }} className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${target === k ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "border-slate-300 text-slate-600"}`}>{l}</button>
            ))}</div>
          )}
          <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
            <li>Download the template; keep ID / mobile columns as Text; dates DD-MM-YYYY.</li>
            <li>Upload — every row is validated before anything is saved.</li>
            <li>Fix listed errors and re-upload. Bad rows are never imported silently.</li>
            {target === "billable_items" && <li>Billable items reference a <b>Connection ID</b> (NX-CONN-…) or a <b>POP Name</b> — import connections & POPs first.</li>}
          </ol>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={downloadTemplate} data-testid="import-template" className="h-9 px-4 rounded-lg border border-slate-300 text-sm font-semibold flex items-center gap-2 hover:bg-slate-50"><Download size={16} /> Template ({target})</button>
            <label className="h-9 px-4 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold flex items-center gap-2 cursor-pointer hover:bg-[var(--brand-hover)]"><Upload size={16} /> Choose file<input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onPick} data-testid="import-file" /></label>
            {file && <span className="text-sm text-slate-500 truncate">{file.name}</span>}
          </div>
          {busy && <div className="text-sm text-slate-500">Validating…</div>}
          {result && (result.ok ? (
            <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm" data-testid="import-ok"><CheckCircle2 size={18} /> {result.valid} row(s) valid — ready to import.</div>
          ) : (
            <div className="space-y-2" data-testid="import-errors">
              <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm"><AlertTriangle size={18} /> {result.errors.length} problem(s) found. Nothing was imported.</div>
              <div className="border border-slate-200 rounded-lg overflow-auto max-h-64">
                <table className="w-full text-sm"><thead><tr className="bg-slate-50 border-b text-left text-[11px] uppercase text-slate-500"><th className="px-3 py-2 w-16">Row</th><th className="px-3 py-2">Column</th><th className="px-3 py-2">Problem</th></tr></thead>
                  <tbody>{result.errors.map((er, i) => (<tr key={i} className="border-b border-slate-100"><td className="px-3 py-2 font-mono-num">{er.row}</td><td className="px-3 py-2">{er.field}</td><td className="px-3 py-2 text-red-600">{er.problem}</td></tr>))}</tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
        <div className="p-5 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border text-sm">Close</button>
          <button onClick={commit} disabled={busy || !result || !result.ok} data-testid="import-commit" className="px-5 h-10 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">Import {result?.ok ? result.valid : ""} row(s)</button>
        </div>
      </div>
    </div>
  );
}
